/**
 * Flight Thank You API
 *
 * Handles flight thank you (booking confirmation) API calls with polling support.
 */
import type { FlightThankYouResponse } from '@contracts';
import { CB_BACKEND_URL } from '@/api/config';
import { logger } from '@/shared/utils/logger';
import { buildHeaders } from '@/api/utils';

// ============================================
// Error Class
// ============================================

export class FlightThankYouError extends Error {
  constructor(
    message: string,
    public readonly statusCode?: number,
    public readonly cause?: unknown,
  ) {
    super(message);
    this.name = 'FlightThankYouError';
  }
}

// ============================================
// Booking Status Types
// ============================================

/**
 * Booking Status Values:
 * 0 - ST_NOT_SET (Not Set)
 * 1 - SUCCESS
 * 2 - FAILED
 * 3 - PARTIAL
 * 4 - NO_ACTION
 * 5 - PENDING
 * 6 - AWAITED
 * 7 - TIMEOUT
 * 8 - USER_SELECTION_INVALID_REQUEST
 * 9 - INITIATED
 */
export type BookingStatusValue = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;

export const BOOKING_STATUS = {
  ST_NOT_SET: 0,
  SUCCESS: 1,
  FAILED: 2,
  PARTIAL: 3,
  NO_ACTION: 4,
  PENDING: 5,
  AWAITED: 6,
  TIMEOUT: 7,
  USER_SELECTION_INVALID_REQUEST: 8,
  INITIATED: 9,
} as const;

export const BOOKING_STATUS_LABELS: Record<BookingStatusValue, string> = {
  0: 'Not Set',
  1: 'Success',
  2: 'Failed',
  3: 'Partial',
  4: 'No Action',
  5: 'Pending',
  6: 'Awaited',
  7: 'Timeout',
  8: 'Invalid Request',
  9: 'Initiated',
};

// Processing states - keep polling
const PROCESSING_STATUSES: readonly BookingStatusValue[] = [
  BOOKING_STATUS.ST_NOT_SET,
  BOOKING_STATUS.PENDING,
  BOOKING_STATUS.AWAITED,
  BOOKING_STATUS.INITIATED,
] as const;

// Final states - stop polling
const FINAL_STATUSES: readonly BookingStatusValue[] = [
  BOOKING_STATUS.SUCCESS,
  BOOKING_STATUS.FAILED,
  BOOKING_STATUS.PARTIAL,
  BOOKING_STATUS.NO_ACTION,
  BOOKING_STATUS.TIMEOUT,
  BOOKING_STATUS.USER_SELECTION_INVALID_REQUEST,
] as const;

/**
 * Check if booking is in a "processing" state (should keep polling)
 */
export function isBookingProcessing(status: string): boolean {
  // Map string status to numeric status
  const statusMap: Record<string, BookingStatusValue> = {
    SUCCESS: BOOKING_STATUS.SUCCESS,
    FAILED: BOOKING_STATUS.FAILED,
    PARTIALLY_SUCCESSFUL: BOOKING_STATUS.PARTIAL,
    PENDING: BOOKING_STATUS.PENDING,
    AWAITED: BOOKING_STATUS.AWAITED,
    INITIATED: BOOKING_STATUS.INITIATED,
  };

  const numericStatus = statusMap[status] ?? BOOKING_STATUS.ST_NOT_SET;
  return PROCESSING_STATUSES.includes(numericStatus);
}

/**
 * Check if booking is in a "final" state (stop polling)
 */
export function isBookingFinal(status: string): boolean {
  const statusMap: Record<string, BookingStatusValue> = {
    SUCCESS: BOOKING_STATUS.SUCCESS,
    FAILED: BOOKING_STATUS.FAILED,
    PARTIALLY_SUCCESSFUL: BOOKING_STATUS.PARTIAL,
    NO_ACTION: BOOKING_STATUS.NO_ACTION,
    TIMEOUT: BOOKING_STATUS.TIMEOUT,
  };

  const numericStatus = statusMap[status] ?? BOOKING_STATUS.ST_NOT_SET;
  return FINAL_STATUSES.includes(numericStatus);
}

// ============================================
// Types
// ============================================

interface ApiErrorInfo {
  code: string;
  message: string;
}

export interface FlightThankYouRequestParams {
  crId: string;
  itId: string;
  sessionId: string;
  currency?: string;
  language?: string;
  region?: string;
}

export interface FlightThankYouApiResponse {
  type: 'flights-thankyou';
  data: FlightThankYouResponse | null;
  success: boolean;
  error?: ApiErrorInfo;
}

interface RawResponse {
  type?: string;
  success?: boolean;
  data?: FlightThankYouResponse | null;
  error?: ApiErrorInfo;
  bookingStatus?: FlightThankYouResponse['bookingStatus'];
  bookingMeta?: FlightThankYouResponse['bookingMeta'];
  itineraries?: FlightThankYouResponse['itineraries'];
  travellers?: FlightThankYouResponse['travellers'];
  amountPaid?: FlightThankYouResponse['amountPaid'];
  footerLinks?: FlightThankYouResponse['footerLinks'];
  ctas?: FlightThankYouResponse['ctas'];
  fareBreakup?: FlightThankYouResponse['fareBreakup'];
}

// ============================================
// API Function
// ============================================

export async function fetchFlightThankYou(
  params: FlightThankYouRequestParams,
): Promise<FlightThankYouApiResponse> {
  const { crId, itId, sessionId, currency = 'INR', language = 'eng', region = 'in' } = params;

  const url = new URL(`${CB_BACKEND_URL}/api/flights/thank-you`);
  url.searchParams.set('crId', crId);
  url.searchParams.set('itId', itId);
  url.searchParams.set('sessionId', sessionId);
  url.searchParams.set('currency', currency);
  url.searchParams.set('language', language);
  url.searchParams.set('region', region);

  logger.warn('[FlightThankYouClient] Fetching:', {
    crId,
    itId,
    sessionId,
    currency,
    language,
    region,
  });

  try {
    const response = await fetch(url.toString(), {
      method: 'GET',
      headers: buildHeaders(),
    });

    const data = (await response.json()) as RawResponse | null;

    if (!response.ok) {
      logger.error('[FlightThankYouClient] API returned error status:', response.status, data);
      if (data?.error) {
        return { type: 'flights-thankyou', success: false, data: null, error: data.error };
      }
      return {
        type: 'flights-thankyou',
        success: false,
        data: null,
        error: { code: 'HTTP_ERROR', message: `HTTP ${response.status}` },
      };
    }

    // API returned error
    if (data?.success === false && data?.error) {
      logger.warn('[FlightThankYouClient] API returned error:', data.error);
      return { type: 'flights-thankyou', success: false, data: null, error: data.error };
    }

    // CBackend returns the transformed response directly
    if (data?.bookingStatus && data.bookingMeta && data.itineraries && data.amountPaid) {
      const thankYouData: FlightThankYouResponse = {
        bookingStatus: data.bookingStatus,
        bookingMeta: data.bookingMeta,
        itineraries: data.itineraries,
        travellers: data.travellers ?? { label: '', travellersList: [] },
        amountPaid: data.amountPaid,
        footerLinks: data.footerLinks ?? {
          icon: '',
          footerText: '',
          footerCta: { label: '', action: { type: 'REDIRECT', url: '' } },
        },
        ctas: data.ctas,
        fareBreakup: data.fareBreakup ?? {
          title: '',
          passengerFares: [],
          grandTotal: {
            amount: 0,
            currency: 'INR',
            amountFormatted: '',
            perPersonLabel: '',
            displayText: '',
          },
        },
      };
      logger.warn('[FlightThankYouClient] Success (direct response)');
      return { type: 'flights-thankyou', success: true, data: thankYouData };
    }

    // Wrapped response format
    if (data?.data) {
      logger.warn('[FlightThankYouClient] Success (wrapped response)');
      return { type: 'flights-thankyou', success: true, data: data.data };
    }

    // No data
    logger.warn('[FlightThankYouClient] Empty response');
    return {
      type: 'flights-thankyou',
      success: false,
      data: null,
      error: { code: 'EMPTY_RESPONSE', message: 'Empty response from server' },
    };
  } catch (error) {
    logger.error('[FlightThankYouClient] Request failed:', error);
    return {
      type: 'flights-thankyou',
      success: false,
      data: null,
      error: {
        code: 'NETWORK_ERROR',
        message: error instanceof Error ? error.message : 'Network request failed',
      },
    };
  }
}
